import { defineBlock } from "eddev/blocks"
import { Container, Grid, Col } from "@components/layout/Grid"
import { cn } from "@utils/tw"
import { Chart as ChartJS, ChartData, ChartOptions, registerables } from "chart.js/auto"
import { Chart } from "react-chartjs-2"
import Papa from "papaparse"
import { ComponentPropsWithoutRef, forwardRef, useEffect, useMemo, useRef, useState } from "react"
import { color } from "@utils/chartjsUtils"
import { hexToRgba } from "@utils/hexToRGBA"

export const meta: BlockMeta = {
  title: "Chart",
  category: "common",
  icon: "chart-bar",
  keywords: ["chart", "graph"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post", "data-embed"],
  },
}

ChartJS.register(...registerables)

export default defineBlock("general/chart", (props) => {
  const [parsedData, setParsedData] = useState<any[] | null>(null)

  useEffect(() => {
    if (!props.data?.mediaItemUrl) return
    const fetchData = async () => {
      const csv = await getSheet(props.data!.mediaItemUrl!)
      setParsedData(csv as [])
    }
    fetchData()
  }, [props.data?.mediaItemUrl])

  async function getSheet(sheetUrl: string) {
    const response = await fetch(sheetUrl.replace("http://", "https://"))
    const reader = response?.body?.getReader()
    const result = await reader?.read()
    const decoder = new TextDecoder("utf-8")
    const csv = decoder.decode(result?.value)
    const results = Papa.parse(csv, { header: true })
    const rows = results.data
    return rows
  }

  return (
    <Container>
      <Grid mode="flex">
        <Col initial={12} tablet={10} tabletOffset={1} laptop={8} laptopOffset={2} desktop={6} desktopOffset={3}>
          <ChartInstance>
            {parsedData && (
              <>
                {props.chartType === "bar" && <BarChart data={parsedData} title={props?.chartTitle ?? undefined} />}
                {props.chartType === "line" && <LineChart data={parsedData} title={props?.chartTitle ?? undefined} />}
                {props.chartType === "doughnut" && (
                  <DoughnutChart data={parsedData} title={props?.chartTitle ?? undefined} />
                )}
              </>
            )}
          </ChartInstance>
        </Col>
      </Grid>
    </Container>
  )
})

const ChartInstance = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "w-full relative transition-all ease-movement duration-300 rounded-md overflow-hidden flex flex-col pt-[calc(100%*(5/8))] my-6",
      "[&_canvas]:absolute [&_canvas]:top-0 [&_canvas]:left-0 [&_canvas]:w-full [&_canvas]:!h-fit",
      className,
    )}
    {...props}
  />
))
ChartInstance.displayName = "ChartInstance"

const colors = ["#FEC32C", "#FFE8B6", "#FFF9EA"]

const BarChart = ({ data, title }: { data: any[]; title?: string }) => {
  const barChartRef = useRef<ChartJS>(null)

  const chartData = useMemo(() => {
    const barData: ChartData<"bar"> = {
      labels: Array.from(new Set(data.flatMap((obj) => Object.keys(obj).filter((key) => key !== "Label")))),
      datasets: data.map((d, i) => ({
        label: d["Label"],
        data: Object.entries(d)
          .filter(([key]) => key !== "Label")
          .map(([, value]) => value) as number[],
        backgroundColor: data.length > 3 ? color(i) : colors[i],
        borderColor: data.length > 3 ? color(i) : colors[i],
        borderRadius: 4,
      })),
    }

    return barData
  }, [data])

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 4 / 2.5,

    font: {
      size: 10,
    },

    scales: {
      y: {
        display: false,
      },
      x: {
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },

        border: {
          display: false,
        },
      },
    },

    plugins: {
      title: {
        display: true,
        position: "top",
        text: title ? title : "",
        font: {
          size: 16,
          family: "Manrope",
        },
      },

      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#00000",
        cornerRadius: 4,
        caretSize: 0,
        caretPadding: 20,
        padding: 8,
        boxPadding: 5,
        usePointStyle: true,

        callbacks: {
          labelColor: (ctx) => {
            return {
              borderColor: ctx.element.options.borderColor,
              backgroundColor: ctx.element.options.borderColor,
            }
          },
          labelPointStyle: () => {
            return {
              pointStyle: "circle",
              rotation: 0,
            }
          },
        },
      },

      legend: {
        position: "bottom",
      },
    },
  }

  return <>{chartData && <Chart type="bar" ref={barChartRef} data={chartData} options={options} />}</>
}

const LineChart = ({ data, title }: { data: any[]; title?: string }) => {
  const lineChartRef = useRef<ChartJS>(null)

  const chartData = useMemo(() => {
    const lineData: ChartData<"line"> = {
      labels: Array.from(new Set(data.flatMap((obj) => Object.keys(obj).filter((key) => key !== "Label")))),
      datasets: data.map((d, i) => ({
        label: d["Label"],
        data: Object.entries(d)
          .filter(([key]) => key !== "Label")
          .map(([, value]) => value) as number[],
        backgroundColor: color(i),
        borderColor: color(i),
        borderRadius: 4,
      })),
    }

    return lineData
  }, [data])

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 4 / 2.5,

    font: {
      size: 10,
    },

    scales: {
      y: {
        display: false,
      },
      x: {
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },

        border: {
          display: false,
        },
      },
    },

    plugins: {
      title: {
        display: true,
        position: "top",
        text: title ? title : "",
        font: {
          size: 16,
          family: "Manrope",
        },
      },

      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#00000",
        cornerRadius: 4,
        caretSize: 0,
        caretPadding: 20,
        padding: 8,
        boxPadding: 5,
        usePointStyle: true,

        callbacks: {
          labelColor: (ctx) => {
            return {
              borderColor: ctx.element.options.borderColor,
              backgroundColor: ctx.element.options.borderColor,
            }
          },
          labelPointStyle: () => {
            return {
              pointStyle: "circle",
              rotation: 0,
            }
          },
        },
      },

      legend: {
        position: "bottom",
      },
    },
  }

  return <>{chartData && <Chart type="line" ref={lineChartRef} data={chartData} options={options} />}</>
}

const DoughnutChart = ({ data, title }: { data: any[]; title?: string }) => {
  const doughnutChartRef = useRef<ChartJS>(null)

  const chartData = useMemo(() => {
    const labels = Array.from(new Set(data.flatMap((obj) => Object.keys(obj).filter((key) => key !== "Label"))))
    const doughnutData: ChartData<"doughnut"> = {
      labels: data.map((d) => d["Label"]).reverse(),
      datasets: labels.reverse().map((year, i) => ({
        label: year,
        data: data.map((d) => Number(d[year] || 0)),
        backgroundColor: data.map((d, k) => hexToRgba(color(k), 1 - i / 5)),
        borderWidth: 3,
        borderRadius: 6,
      })),
    }

    return doughnutData
  }, [data])

  const options: ChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: 4 / 2.5,

    font: {
      size: 10,
    },

    scales: {
      y: {
        display: false,
      },
      x: {
        display: false,
      },
    },

    plugins: {
      title: {
        display: true,
        position: "top",
        text: title ? title : "",
        font: {
          size: 16,
          family: "Manrope",
        },
      },

      tooltip: {
        mode: "index",
        intersect: false,
        backgroundColor: "#00000",
        cornerRadius: 4,
        caretSize: 0,
        caretPadding: 20,
        padding: 8,
        boxPadding: 5,
        usePointStyle: true,

        callbacks: {
          labelColor: (ctx) => {
            return {
              borderColor: ctx.element.options.backgroundColor,
              backgroundColor: ctx.element.options.backgroundColor,
            }
          },
          labelPointStyle: () => {
            return {
              pointStyle: "circle",
              rotation: 0,
            }
          },
        },
      },

      legend: {
        position: "bottom",
      },
    },
  }

  return <>{chartData && <Chart type="doughnut" ref={doughnutChartRef} data={chartData} options={options} />}</>
}
